
export interface DisplayConfig {
  name: string;
  label: string;
  description?: string;
  copyable?: boolean;
  format?: (value: any) => string;
  order: number;
}

// Base display configs that are common across all models
const baseDisplayConfigs: DisplayConfig[] = [
  {
    name: 'prompt_template',
    label: 'Prompt Template',
    description: 'The template for generating content with customizable sections in [brackets]',
    copyable: true,
    order: 10,
  },
  {
    name: 'tips',
    label: 'Tips & Instructions',
    description: 'Additional tips and instructions for getting the best results',
    copyable: true,
    order: 20,
  },
  {
    name: 'prompt_example',
    label: 'Prompt Examples',
    description: '',
    copyable: true,
    order: 999,
  },
];

// Model-specific display configurations
export const modelDisplayConfigs: Record<string, DisplayConfig[]> = {
  'stable-diffusion': [
    ...baseDisplayConfigs,
    {
      name: 'image_width',
      label: 'Image Width',
      order: 30,
    },
    {
      name: 'image_height',
      label: 'Image Height',
      order: 40,
    },
    {
      name: 'negative_prompt',
      label: 'Negative Prompt',
      description: 'Elements to exclude from the generation',
      copyable: true,
      order: 50,
    },
    {
      name: 'stable_diffusion_sampler',
      label: 'Sampler',
      order: 60,
    },
    {
      name: 'hidden_fields.steps',
      label: 'Steps',
      order: 70,
    },
    {
      name: 'hidden_fields.cfg_scale',
      label: 'CFG Scale',
      order: 80,
    },
    {
      name: 'seed',
      label: 'Seed',
      copyable: true,
      order: 90,
    },
  ],
  'ideogram-ai': [
    ...baseDisplayConfigs,
    {
      name: 'hidden_fields.magic_prompt',
      label: 'Magic Prompt',
      order: 30,
    },
    {
      name: 'default_image_size',
      label: 'Aspect Ratio',
      order: 40,
    },
    {
      name: 'hidden_fields.style_type',
      label: 'Style Type',
      order: 50,
    },
    {
      name: 'negative_prompt',
      label: 'Negative Prompt',
      copyable: true,
      order: 60,
    },
  ],
  'kling-ai': [
    ...baseDisplayConfigs,
    {
      name: 'duration',
      label: 'Duration',
      order: 30,
    },
    {
      name: 'default_image_size',
      label: 'Aspect Ratio',
      order: 40,
    },
    {
      name: 'resolution',
      label: 'Resolution',
      order: 50,
    },
    {
      name: 'mode',
      label: 'Mode',
      order: 60,
    },
    {
      name: 'negative_prompt',
      label: 'Negative Prompt',
      copyable: true,
      order: 70,
    },
  ],
  'claude-ai': [
    ...baseDisplayConfigs,
    {
      name: 'max_tokens',
      label: 'Max Tokens',
      order: 30,
    },
    {
      name: 'temperature',
      label: 'Temperature',
      format: (value: number) => value.toFixed(1),
      order: 40,
    },
  ],
  'llama-ai': [
    ...baseDisplayConfigs,
    {
      name: 'max_tokens',
      label: 'Max Tokens',
      order: 30,
    },
    {
      name: 'temperature',
      label: 'Temperature',
      format: (value: number) => value.toFixed(1),
      order: 40,
    },
    {
      name: 'top_p',
      label: 'Top P',
      format: (value: number) => value.toFixed(2),
      order: 50,
    },
    {
      name: 'repetition_penalty',
      label: 'Repetition Penalty',
      format: (value: number) => value.toFixed(1),
      order: 60,
    },
  ],
  'gemini': [
    ...baseDisplayConfigs,
    {
      name: 'max_tokens',
      label: 'Max Tokens',
      order: 30,
    },
    {
      name: 'temperature',
      label: 'Temperature',
      format: (value: number) => value.toFixed(1),
      order: 40,
    },
  ],
  'mistral': [
    ...baseDisplayConfigs,
    {
      name: 'max_tokens',
      label: 'Max Tokens',
      order: 30,
    },
    {
      name: 'temperature',
      label: 'Temperature',
      format: (value: number) => value.toFixed(1),
      order: 40,
    },
    {
      name: 'top_p',
      label: 'Top P',
      format: (value: number) => value.toFixed(2),
      order: 50,
    },
    {
      name: 'repetition_penalty',
      label: 'Repetition Penalty',
      format: (value: number) => value.toFixed(1),
      order: 60,
    },
    {
      name: 'hidden_fields.presence_penalty',
      label: 'Presence Penalty',
      format: (value: number) => value.toFixed(2),
      order: 70,
    },
    {
      name: 'hidden_fields.stop_sequences',
      label: 'Stop Sequences',
      order: 80,
    },
  ],
  'flux-ai': [
    ...baseDisplayConfigs,
    {
      name: 'default_image_size',
      label: 'Aspect Ratio',
      order: 40,
    },
  ],
  'leonardo-ai': [
    ...baseDisplayConfigs,
    {
      name: 'hidden_fields.style_preset',
      label: 'Style Preset',
      order: 30,
    },
    {
      name: 'image_width',
      label: 'Image Width',
      order: 40,
    },
    {
      name: 'image_height',
      label: 'Image Height',
      order: 50,
    },
    {
      name: 'guidance_scale',
      label: 'Guidance Scale',
      format: (value: number) => value.toFixed(1),
      order: 60,
    },
    {
      name: 'negative_prompt',
      label: 'Negative Prompt',
      copyable: true,
      order: 70,
    },
    {
      name: 'seed',
      label: 'Seed',
      copyable: true,
      order: 80,
    },
    {
      name: 'hidden_fields.photoreal',
      label: 'PhotoReal',
      order: 90,
    },
  ],
  'leonardo-phoenix': [
    ...baseDisplayConfigs,
    {
      name: 'hidden_fields.style_preset',
      label: 'Style Preset',
      order: 30,
    },
    {
      name: 'prompt_enhance',
      label: 'Prompt Enhance',
      order: 40,
    },
    {
      name: 'contrast',
      label: 'Contrast',
      order: 50,
    },
    {
      name: 'generation_mode',
      label: 'Generation Mode',
      order: 60,
    },
    {
      name: 'image_width',
      label: 'Image Width',
      order: 70,
    },
    {
      name: 'image_height',
      label: 'Image Height',
      order: 80,
    },
    {
      name: 'negative_prompt',
      label: 'Negative Prompt',
      copyable: true,
      order: 90,
    },
    {
      name: 'seed',
      label: 'Seed',
      copyable: true,
      order: 100,
    },
  ],
  'sora': [
    ...baseDisplayConfigs,
    {
      name: 'duration',
      label: 'Duration',
      order: 30,
    },
    {
      name: 'default_image_size',
      label: 'Aspect Ratio',
      order: 40,
    },
    {
      name: 'resolution',
      label: 'Resolution',
      order: 50,
    },
    {
      name: 'generation_mode',
      label: 'Generation Mode',
      order: 60,
    },
    {
      name: 'input_image',
      label: 'Input Image',
      order: 10,
    },
  ],
};

export const getModelDisplayConfig = (modelSlug?: string): DisplayConfig[] => {
  if (modelSlug && modelDisplayConfigs[modelSlug]) {
    return [...modelDisplayConfigs[modelSlug]].sort((a, b) => a.order - b.order);
  }
  return [...baseDisplayConfigs].sort((a, b) => a.order - b.order);
};
